import {
	ActionMap,
	FormContext_Action_PayloadMap,
	FormContext_Actions,
	formContext_reducer,
	isFormContext_Action_PayloadMap,
} from '@/components/forms/FormContext';
import { RepairFormState, StepMap } from './constants';
import { match } from '@/helpers/match';
import logError from '@/helpers/logError';
import {
	AddressStepActionMap,
	AddressStepActions,
	addressStepReducer,
	isAddressStepActionMap,
} from './steps/AddressStep/state';
import {
	ContactInfoStepActionMap,
	ContactInfoStepActions,
	contactInfoStepReducer,
	isContactInfoStepActionMap,
} from './steps/ContactInfoStep/state';
import {
	isSystemIssuesStepActionMap,
	SystemIssuesStepActionMap,
	SystemIssuesStepActions,
	systemIssuesStepReducer,
} from './steps/SystemIssuesStep/state';
import {
	isPictureStepActionMap,
	PictureStepActionMap,
	PictureStepActions,
	pictureStepReducer,
} from './steps/PictureStep/state';
import userProfile from '@/helpers/userProfile';
import {
	isMessageStepActionMap,
	MessageStepActionMap,
	MessageStepActions,
	messageStepReducer,
} from './steps/MessageStep/state';
import {
	isSystemAgeStepActionMap,
	SystemAgeStepActionMap,
	SystemAgeStepActions,
	systemAgeStepReducer,
} from './steps/SystemAgeStep/state';
import {
	isBuyingDecisionStepActionMap,
	BuyingDecisionStepActionMap,
	BuyingDecisionStepActions,
	buyingDecisionStepReducer,
} from './steps/BuyingDecisionStep/state';
import {
	isUrgencyStepActionMap,
	UrgencyStepActionMap,
	UrgencyStepActions,
	UrgencyStepReducer,
} from './steps/UrgencyStep/state';
/**
 * These are not step specific actions, they are actions that can be handled by
 * the form context
 */
export const RepairFormActions = {
	SET_HUBSPOT_DEAL_ID: 'SET_HUBSPOT_DEAL_ID',
	HYDRATE_STATE: 'HYDRATE_STATE',
} as const;

/**
 * The payloads for the non-step specific actions
 */
export interface RepairActionPayload {
	[RepairFormActions.SET_HUBSPOT_DEAL_ID]: { hubspotDealId: string };
	[RepairFormActions.HYDRATE_STATE]: RepairFormState;
}

/**
 * The map for what payload corresponds to what action also, not step specific
 */
export type RepairNoStepActionMap =
	ActionMap<RepairActionPayload>[keyof ActionMap<RepairActionPayload>];

/**
 * The map for all the actions that can be handled by the reducer. If you add a
 * new step, you will need to add the action map for that step here.
 */
export type RepairFormActionMap =
	| FormContext_Action_PayloadMap<StepMap>
	| UrgencyStepActionMap
	| AddressStepActionMap
	| ContactInfoStepActionMap
	| SystemIssuesStepActionMap
	| PictureStepActionMap
	| BuyingDecisionStepActionMap
	| MessageStepActionMap
	| SystemAgeStepActionMap
	| RepairNoStepActionMap;

/**
 * The rootReducer for the form. This is where all the actions are handled. FYI
 * order does not matter.
 *
 * If you add a new step, you will need to add the reducer for that step here.
 */
export const rootReducer = (
	state: RepairFormState,
	action: RepairFormActionMap,
): RepairFormState => {
	const [, updateProfile] = userProfile();
	const updatedState = match<RepairFormState>(
		[
			// This is the only action the form context will handle
			action.type in FormContext_Actions,
			(): RepairFormState => {
				if (!isFormContext_Action_PayloadMap<StepMap>(action)) return state;
				return formContext_reducer<StepMap, RepairFormState>(state, action);
			},
		],
		[
			action.type in UrgencyStepActions,
			(): RepairFormState => {
				if (!isUrgencyStepActionMap(action)) return state;
				return UrgencyStepReducer(state, action);
			},
		],
		[
			action.type in AddressStepActions,
			(): RepairFormState => {
				if (!isAddressStepActionMap(action)) return state;
				return addressStepReducer(state, action);
			},
		],
		[
			action.type in ContactInfoStepActions,
			(): RepairFormState => {
				if (!isContactInfoStepActionMap(action)) return state;
				return contactInfoStepReducer(state, action);
			},
		],
		[
			action.type in SystemIssuesStepActions,
			(): RepairFormState => {
				if (!isSystemIssuesStepActionMap(action)) return state;
				return systemIssuesStepReducer(state, action);
			},
		],
		[
			action.type in PictureStepActions,
			(): RepairFormState => {
				if (!isPictureStepActionMap(action)) return state;
				return pictureStepReducer(state, action);
			},
		],
		[
			action.type in BuyingDecisionStepActions,
			(): RepairFormState => {
				if (!isBuyingDecisionStepActionMap(action)) return state;
				return buyingDecisionStepReducer(state, action);
			},
		],
		[
			action.type in MessageStepActions,
			(): RepairFormState => {
				if (!isMessageStepActionMap(action)) return state;
				return messageStepReducer(state, action);
			},
		],
		[
			action.type in SystemAgeStepActions,
			(): RepairFormState => {
				if (!isSystemAgeStepActionMap(action)) return state;
				return systemAgeStepReducer(state, action);
			},
		],
		[
			action.type in RepairFormActions,
			(): RepairFormState => {
				switch (action.type) {
					case RepairFormActions.SET_HUBSPOT_DEAL_ID:
						updateProfile('dealId', action.payload.hubspotDealId);
						return {
							...state,
							hubspotDealId: action.payload.hubspotDealId,
						};
					case RepairFormActions.HYDRATE_STATE:
						return {
							...state,
							...action.payload,
							/**
							 * We don't want to hydrate the steps because they have functions, which are not serializable
							 * so we don't want to overwrite them with the ones pulled from the server because the server
							 * only contains serializable data.
							 */
							steps: state.steps,
						};

					default:
						return state;
				}
			},
		],
	);
	if (!updatedState)
		throw logError(new Error('rootReducer failed to return a state'));
	return updatedState;
};
